import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type TicketType = {
  code: string;
  name: string;
  nameZh: string;
  genderLabel: string;
  priceHkd: number;
  priceLabel: string;
  includes: string;
  includesZh: string;
  bookingChannel: string;
  badge?: string;
  isActive: boolean;
  soldOut?: boolean;
};

type EventFaq = {
  question: string;
  answer: string;
};

type EventDetail = {
  slug: string;
  shortCode: string;
  showShortCode?: boolean;
  title: string;
  titleZh: string;
  heroTitle: string;
  heroSubtitle: string;
  summary: string;
  memberPrivilege: string;
  venue: string;
  address: string;
  organiserContact: string;
  startAt: string;
  endAt: string;
  doorsOpenAt: string;
  dateLabel: string;
  timeLabel: string;
  entryAgePolicy: string;
  dressCode: string;
  doorPolicy: string;
  cancellationPolicy: string;
  transferPolicy: string;
  weatherPolicy: string;
  highlights: string[];
  autoApprove?: boolean;
  photoUrl?: string | null;
  ticketTypes: TicketType[];
  faqs: EventFaq[];
  paymentInstruction?: string | null;
  paymentQrUrl?: string | null;
  paymentMethods?: {
    id: string;
    name: string;
    description: string;
    photoUrl?: string | null;
  }[];
  discountCodePolicy?: string;
  rightOfAdmission?: string;
  emailNotificationNote?: string;
  discountCodes?: { code: string; discountType: string; discountValue: number }[];
  registrationOpen?: boolean;
  customFields?: {
    id: string;
    fieldKey: string;
    labelEn: string;
    labelZh: string;
    fieldType: string;
    options: { en: string; zh: string }[];
    required: boolean;
    parentFieldKey?: string | null;
    parentValue?: string | null;
  }[];
};

type RegistrationResponse = {
  id: string;
  status: string;
  ticketTypeNameZh: string;
};

type FormState = {
  ticketTypeCode: string;
  fullName: string;
  displayName: string;
  phone: string;
  email: string;
  discountCode: string;
  paymentMethod: string;
  receiptFileName: string;
  receiptMimeType: string;
  receiptDataUrl: string;
  customValues: Record<string, string>;
};

const initialForm: FormState = {
  ticketTypeCode: "",
  fullName: "",
  displayName: "",
  phone: "",
  email: "",
  discountCode: "",
  paymentMethod: "",
  receiptFileName: "",
  receiptMimeType: "",
  receiptDataUrl: "",
  customValues: {},
};

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("invalid_reader_result"));
    };
    reader.onerror = () => reject(new Error("file_read_failed"));
    reader.readAsDataURL(file);
  });

const isCustomFieldVisible = (cf: EventDetail["customFields"][0], customValues: Record<string, string>) => {
  if (!cf.parentFieldKey) return true;
  const parentVal = customValues[cf.parentFieldKey];
  return parentVal !== undefined && parentVal === cf.parentValue;
};

export default function EventDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<RegistrationResponse | null>(null);
  const [form, setForm] = useState<FormState>(initialForm);
  const [step, setStep] = useState(1);
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string; discountAmount: number } | null>(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      setLoading(true);
      setError("");
      setSuccess(null);
      try {
        const res = await fetch(`/api/events/${encodeURIComponent(slug)}`);
        const data = (await res.json()) as { ok?: boolean; event?: EventDetail; error?: string };
        if (!res.ok || !data.ok || !data.event) {
          throw new Error(data.error || "failed");
        }
        setEvent(data.event);
        setForm((prev) => ({
          ...prev,
          ticketTypeCode: prev.ticketTypeCode || data.event?.ticketTypes?.[0]?.code || "",
          paymentMethod: prev.paymentMethod || data.event?.paymentMethods?.[0]?.name || "",
        }));
      } catch {
        setError("Failed to load this event. / 無法載入此活動。");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [slug]);

  const selectedTicket = useMemo(
    () => event?.ticketTypes.find((ticket) => ticket.code === form.ticketTypeCode) ?? null,
    [event, form.ticketTypeCode],
  );

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleReceiptChange = async (eventInput: ChangeEvent<HTMLInputElement>) => {
    const file = eventInput.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file for the payment receipt. / 請上傳付款收據圖片。");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Receipt image must be 5MB or smaller. / 收據圖片不得超過 5MB。");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setError("");
      setForm((prev) => ({
        ...prev,
        receiptDataUrl: dataUrl,
        receiptFileName: file.name,
        receiptMimeType: file.type,
      }));
    } catch {
      setError("Failed to read the receipt image. Please try another file. / 讀取收據圖片失敗，請嘗試其他檔案。");
    }
  };

  const handleValidateDiscount = async () => {
    if (!slug || !form.discountCode.trim()) return;
    setValidatingDiscount(true);
    setError("");
    try {
      const res = await fetch(`/api/events/${encodeURIComponent(slug)}/validate-discount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: form.discountCode.trim() }),
      });
      const data = (await res.json()) as { ok?: boolean; code?: string; discountAmount?: number; error?: string };
      if (!res.ok || !data?.ok || !data.code) {
        throw new Error(data?.error || "Invalid code");
      }
      setAppliedDiscount({ code: data.code, discountAmount: data.discountAmount ?? 0 });
    } catch (err) {
      setAppliedDiscount(null);
      setError(err instanceof Error ? err.message : "Failed to validate discount code / 折扣碼驗證失敗");
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleSubmit = async (submitEvent: React.SyntheticEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();
    if (!slug) return;

    let hasError = false;
    if (!form.ticketTypeCode || !form.fullName || !form.phone || !form.email || !form.receiptDataUrl) {
      hasError = true;
    }
    if ((event?.paymentMethods?.length ?? 0) > 0 && !form.paymentMethod) {
      hasError = true;
    }

    const visibleCustomFields = event?.customFields?.filter((cf) => isCustomFieldVisible(cf, form.customValues)) ?? [];
    for (const cf of visibleCustomFields) {
      if (cf.required) {
        const val = form.customValues[cf.fieldKey];
        if (!val || !val.trim()) {
          hasError = true;
        }
      }
    }

    if (hasError) {
      setError("Please complete all required fields and upload your receipt. / 請完成所有必填欄位並上傳收據。");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(`/api/events/${encodeURIComponent(slug)}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        registration?: RegistrationResponse;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.registration) {
        throw new Error(data.error || "failed");
      }
      setSuccess(data.registration);
      setForm((prev) => ({ ...initialForm, ticketTypeCode: prev.ticketTypeCode }));
    } catch (submitError) {
      const message =
        submitError instanceof Error ? submitError.message : "Failed to submit registration. / 提交報名失敗。";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="bg-white rounded-3xl border border-gray-100 p-8 text-gray-500">
              Loading event... / 載入中...
            </div>
          ) : !event ? (
            <div className="bg-red-50 rounded-3xl border border-red-100 p-8 text-red-600">
              {error || "Event not found. / 找不到活動。"}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-8 items-start">
              <div className="space-y-6">
                <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                  {event.photoUrl ? (
                    <div className="rounded-2xl overflow-hidden mb-5">
                      <img
                        src={event.photoUrl}
                        alt={event.titleZh}
                        className="w-full aspect-[2/1] object-cover rounded-2xl"
                      />
                    </div>
                  ) : null}
                  {event.showShortCode !== false && event.shortCode ? (
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#9B72FF] mb-3">
                      {event.shortCode}
                    </p>
                  ) : null}
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-1">{event.titleZh}</h1>
                  <p className="text-lg md:text-xl font-semibold text-gray-800 mb-2">{event.heroTitle}</p>
                  {event.heroSubtitle ? (
                    <p className="text-base md:text-lg text-gray-600 italic mb-3">{event.heroSubtitle}</p>
                  ) : null}
                  <p className="text-gray-500 leading-relaxed mb-5">{event.summary}</p>
                  {event.highlights && event.highlights.length > 0 ? (
                    <div className="mb-5">
                      <p className="text-sm font-semibold uppercase tracking-widest text-[#9B72FF] mb-3">
                        Highlights / 精彩預告
                      </p>
                      <ul className="space-y-2.5">
                        {event.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-2.5">
                            <svg
                              className="w-4 h-4 text-[#9B72FF] mt-0.5 flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
                            </svg>
                            <span className="text-sm text-gray-700 leading-relaxed">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {event.memberPrivilege ? (
                    <div className="rounded-2xl bg-[#F5F0FF] border border-[#E6D9FF] p-5">
                      <p className="text-sm font-semibold text-[#5B3FA0] mb-2">
                        Aurays Member Exclusive Privilege / 會員獨家禮遇
                      </p>
                      <p className="text-sm leading-relaxed text-[#4A2D8A]">{event.memberPrivilege}</p>
                    </div>
                  ) : null}
                </div>

                {event.dateLabel || event.timeLabel || event.venue || event.organiserContact || event.address ? (
                  <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-5">活動詳情 Event Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {event.dateLabel ? (
                        <div className="rounded-2xl bg-gray-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                            Date / 日期
                          </p>
                          <p className="text-sm font-medium text-gray-800">{event.dateLabel}</p>
                        </div>
                      ) : null}
                      {event.timeLabel ? (
                        <div className="rounded-2xl bg-gray-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                            Time / 時間
                          </p>
                          <p className="text-sm font-medium text-gray-800">{event.timeLabel}</p>
                        </div>
                      ) : null}
                      {event.venue ? (
                        <div className="rounded-2xl bg-gray-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                            Venue / 地點
                          </p>
                          <p className="text-sm font-medium text-gray-800">{event.venue}</p>
                        </div>
                      ) : null}
                      {event.organiserContact ? (
                        <div className="rounded-2xl bg-gray-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                            Enquiry / 查詢
                          </p>
                          <p className="text-sm font-medium text-gray-800">{event.organiserContact}</p>
                        </div>
                      ) : null}
                    </div>
                    {event.address ? (
                      <div className="mt-4 rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                          Address / 地址
                        </p>
                        <p className="text-sm font-medium text-gray-800">{event.address}</p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {event.ticketTypes.length > 0 ? (
                  <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-5">票務詳情 Ticket Options</h2>
                    <div className="space-y-4">
                      {event.ticketTypes.map((ticket) => (
                        <div
                          key={ticket.code}
                          className={`rounded-2xl border p-5 ${ticket.soldOut ? "border-gray-200 opacity-60" : "border-gray-100"}`}
                        >
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              <p className="text-lg font-semibold text-gray-900">{ticket.nameZh}</p>
                              <p className="text-sm text-gray-500">{ticket.name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-gray-900">{ticket.priceLabel}</p>
                              <div className="flex flex-col gap-1 mt-2 items-end">
                                {ticket.soldOut ? (
                                  <span className="inline-flex rounded-full bg-red-50 text-red-600 text-xs font-semibold px-3 py-1.5">
                                    Sold Out / 已售罄
                                  </span>
                                ) : ticket.badge ? (
                                  <span className="inline-flex rounded-full bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-3 py-1.5">
                                    {ticket.badge}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-3 text-sm">
                            {ticket.includesZh ? (
                              <div className="rounded-2xl bg-gray-50 p-4">
                                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                                  Includes / 包括
                                </p>
                                <p className="font-medium text-gray-800">{ticket.includesZh}</p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {event.doorPolicy ||
                event.entryAgePolicy ||
                event.dressCode ||
                event.cancellationPolicy ||
                event.transferPolicy ||
                event.weatherPolicy ? (
                  <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-5">入場守則 Entry Policy</h2>
                    <div className="space-y-4 text-sm leading-relaxed text-gray-600">
                      {event.doorPolicy ? (
                        <p>
                          <span className="font-semibold text-gray-900">Door Policy / 入場時間:</span>{" "}
                          {event.doorPolicy}
                        </p>
                      ) : null}
                      {event.entryAgePolicy ? (
                        <p>
                          <span className="font-semibold text-gray-900">Age Restriction / 年齡限制:</span>{" "}
                          {event.entryAgePolicy}
                        </p>
                      ) : null}
                      {event.dressCode ? (
                        <p>
                          <span className="font-semibold text-gray-900">Dress Code / 穿搭指南:</span> {event.dressCode}
                        </p>
                      ) : null}
                      {event.cancellationPolicy ? (
                        <p>
                          <span className="font-semibold text-gray-900">Cancellation / 取消政策:</span>{" "}
                          {event.cancellationPolicy}
                        </p>
                      ) : null}
                      {event.transferPolicy ? (
                        <p>
                          <span className="font-semibold text-gray-900">Transfer / 名額轉讓:</span>{" "}
                          {event.transferPolicy}
                        </p>
                      ) : null}
                      {event.weatherPolicy ? (
                        <p>
                          <span className="font-semibold text-gray-900">Weather / 惡劣天氣:</span> {event.weatherPolicy}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {event.faqs.length > 0 ? (
                  <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-5">常見問題 FAQ</h2>
                    <div className="space-y-4">
                      {event.faqs.map((faq) => (
                        <div key={faq.question} className="rounded-2xl bg-gray-50 p-5">
                          <p className="font-semibold text-gray-900 mb-2">{faq.question}</p>
                          <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {event.emailNotificationNote || event.discountCodePolicy || event.rightOfAdmission ? (
                  <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-5">補充事項 Additional Notes</h2>
                    <div className="space-y-4 text-sm leading-relaxed text-gray-600">
                      {event.emailNotificationNote ? <p>{event.emailNotificationNote}</p> : null}
                      {event.discountCodePolicy ? (
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Discount Code / 折扣碼</p>
                          <p>{event.discountCodePolicy}</p>
                        </div>
                      ) : null}
                      {event.rightOfAdmission ? (
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Right of Admission / 入場權利</p>
                          <p>{event.rightOfAdmission}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lg:sticky lg:top-28" id="register">
                <div className="bg-white rounded-3xl border border-gray-100 p-7 md:p-8">
                  {success ? (
                    <div>
                      <div className="w-14 h-14 rounded-full bg-[#E6D9FF] text-[#5B3FA0] flex items-center justify-center text-2xl mb-5">
                        ✓
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">登記已提交 Registration submitted</h2>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        {event.autoApprove
                          ? "多謝你！你的報名已成功確認，電子門票已即時生成，可於下方開啟。"
                          : "你的收據已上傳，現正等待管理員審核。審核通過後，電子門票會自動生成，並可透過電郵連結開啟。"}{" "}
                        {event.autoApprove
                          ? "Thank you! Your registration is confirmed and your e-ticket is ready."
                          : "Your receipt has been uploaded and is now pending admin review. Once approved, your e-ticket is"}{" "}
                        {event.autoApprove ? "" : "generated automatically and can be opened from the email link."}
                      </p>
                      <div className="rounded-2xl bg-gray-50 p-4 space-y-2 text-sm">
                        <p>
                          <span className="font-semibold text-gray-900">Reference / 編號:</span> {success.id}
                        </p>
                        <p>
                          <span className="font-semibold text-gray-900">Status / 狀態:</span> {success.status}
                        </p>
                        <p>
                          <span className="font-semibold text-gray-900">Ticket / 門票:</span> {success.ticketTypeNameZh}
                        </p>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => setSuccess(null)}
                          className="bg-gray-900 text-white font-semibold px-5 py-3 rounded-full hover:bg-gray-700 transition-colors"
                        >
                          提交另一個 Submit Another
                        </button>
                        <Link
                          to="/events"
                          className="bg-gray-100 text-gray-800 font-semibold px-5 py-3 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          返回活動 Back to Events
                        </Link>
                      </div>
                    </div>
                  ) : event.registrationOpen === false ? (
                    <div>
                      <div className="w-14 h-14 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-2xl mb-5">
                        ✕
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">報名已截止 Registration Closed</h2>
                      <p className="text-sm text-gray-500 leading-relaxed mb-4">
                        感謝你的關注，此活動的報名已經截止。期待在未來活動中見到你！
                      </p>
                      <p className="text-sm text-gray-500 leading-relaxed mb-5">
                        Thank you for your interest. Registration for this event is now closed. We look forward to
                        seeing you at future events!
                      </p>
                      <Link
                        to="/events"
                        className="inline-block bg-gray-900 text-white font-semibold px-5 py-3 rounded-full hover:bg-gray-700 transition-colors"
                      >
                        返回活動 Back to Events
                      </Link>
                    </div>
                  ) : (
                    <>
                      {/* Step indicator */}
                      <div className="flex items-center gap-2 mb-6">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 1 ? "bg-gray-900 text-white" : "bg-[#E6D9FF] text-[#5B3FA0]"}`}
                        >
                          1
                        </div>
                        <div className={`flex-1 h-1 rounded-full ${step === 2 ? "bg-[#9B72FF]" : "bg-gray-200"}`} />
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step === 2 ? "bg-gray-900 text-white" : "bg-gray-200 text-gray-400"}`}
                        >
                          2
                        </div>
                      </div>

                      {step === 1 ? (
                        <>
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">選擇門票 Select Ticket</h2>
                          <p className="text-sm text-gray-500 leading-relaxed mb-6">
                            選擇門票類型並填寫個人資料。Choose your ticket type and fill in your details.
                          </p>

                          <div className="space-y-5">
                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-3">
                                選擇門票類型 Pick your ticket
                              </label>
                              <div className="space-y-3">
                                {event.ticketTypes.map((ticket) => (
                                  <button
                                    key={ticket.code}
                                    type="button"
                                    onClick={() => !ticket.soldOut && handleChange("ticketTypeCode", ticket.code)}
                                    className={`w-full text-left rounded-2xl border p-4 transition-colors ${ticket.soldOut ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed" : form.ticketTypeCode === ticket.code ? "border-[#9B72FF] bg-[#F5F0FF]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div>
                                        <p className="font-semibold text-gray-900">{ticket.nameZh}</p>
                                        <p className="text-sm text-gray-500">{ticket.name}</p>
                                        {ticket.includes ? (
                                          <p className="text-xs text-gray-500 mt-1">{ticket.includes}</p>
                                        ) : null}
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm font-bold text-gray-900 shrink-0">{ticket.priceLabel}</p>
                                        {ticket.soldOut ? (
                                          <span className="inline-flex mt-2 rounded-full bg-red-50 text-red-600 text-xs font-semibold px-3 py-1">
                                            Sold Out / 已售罄
                                          </span>
                                        ) : ticket.badge ? (
                                          <span className="inline-flex mt-2 rounded-full bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-3 py-1">
                                            {ticket.badge}
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-2">姓名 Full Name *</label>
                              <input
                                type="text"
                                required
                                value={form.fullName}
                                onChange={(e) => handleChange("fullName", e.target.value)}
                                className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                                placeholder="Your legal name for check-in / 入場登記姓名"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-2">
                                顯示名稱 Display Name
                              </label>
                              <input
                                type="text"
                                value={form.displayName}
                                onChange={(e) => handleChange("displayName", e.target.value)}
                                className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                                placeholder="Optional nickname / 選填暱稱"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-2">
                                電話號碼 Phone Number *
                              </label>
                              <input
                                type="tel"
                                required
                                value={form.phone}
                                onChange={(e) => handleChange("phone", e.target.value)}
                                className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                                placeholder="+852 ..."
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-2">電子郵件 Email *</label>
                              <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => handleChange("email", e.target.value)}
                                className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                                placeholder="you@example.com"
                              />
                            </div>

                            {event.discountCodes && event.discountCodes.length > 0 ? (
                              <div>
                                <label className="block text-sm font-semibold text-gray-800 mb-2">
                                  Discount Code / 折扣碼
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={form.discountCode}
                                    onChange={(e) => {
                                      handleChange("discountCode", e.target.value);
                                      setAppliedDiscount(null);
                                    }}
                                    className="flex-1 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                                    placeholder="Enter discount code"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleValidateDiscount}
                                    disabled={validatingDiscount || !form.discountCode.trim()}
                                    className="px-4 py-3 text-sm font-semibold text-white bg-[#9B72FF] hover:bg-[#7C55CC] rounded-2xl cursor-pointer disabled:opacity-50 shrink-0"
                                  >
                                    {validatingDiscount ? "Checking..." : "Apply"}
                                  </button>
                                </div>
                                {appliedDiscount ? (
                                  <p className="mt-2 text-sm text-green-600">
                                    Code <strong>{appliedDiscount.code}</strong> applied: -HKD{" "}
                                    {appliedDiscount.discountAmount}
                                  </p>
                                ) : null}
                              </div>
                            ) : null}

                            {event.customFields && event.customFields.length > 0 ? (
                              <div className="space-y-4">
                                {event.customFields
                                  .filter((cf) => isCustomFieldVisible(cf, form.customValues))
                                  .map((cf) => (
                                    <div key={cf.id}>
                                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                                        {cf.labelEn && cf.labelZh
                                          ? `${cf.labelEn} / ${cf.labelZh}`
                                          : cf.labelEn || cf.labelZh || ""}
                                        {cf.required ? <span className="text-red-500 ml-1">*</span> : null}
                                      </label>
                                      {cf.fieldType === "dropdown" ? (
                                        <select
                                          value={form.customValues[cf.fieldKey] || ""}
                                          onChange={(e) => {
                                            const v: Record<string, string> = { ...form.customValues };
                                            v[cf.fieldKey] = e.target.value;
                                            setForm((prev) => ({ ...prev, customValues: v }));
                                          }}
                                          className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                                        >
                                          <option value="">-- Select / 請選擇 --</option>
                                          {cf.options.map((opt, idx) => (
                                            <option key={idx} value={opt.en}>
                                              {opt.en && opt.zh ? `${opt.en} / ${opt.zh}` : opt.en || opt.zh || ""}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={form.customValues[cf.fieldKey] || ""}
                                          onChange={(e) => {
                                            const v: Record<string, string> = { ...form.customValues };
                                            v[cf.fieldKey] = e.target.value;
                                            setForm((prev) => ({ ...prev, customValues: v }));
                                          }}
                                          className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                                          placeholder={
                                            cf.labelEn && cf.labelZh
                                              ? `${cf.labelEn} / ${cf.labelZh}`
                                              : cf.labelEn || cf.labelZh || ""
                                          }
                                        />
                                      )}
                                    </div>
                                  ))}
                              </div>
                            ) : null}

                            {error ? (
                              <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
                            ) : null}

                            <button
                              type="button"
                              onClick={() => {
                                let hasError = false;
                                if (!form.ticketTypeCode || !form.fullName || !form.phone || !form.email) {
                                  hasError = true;
                                }

                                const visibleCustomFields =
                                  event.customFields?.filter((cf) => isCustomFieldVisible(cf, form.customValues)) ?? [];
                                for (const cf of visibleCustomFields) {
                                  if (cf.required) {
                                    const val = form.customValues[cf.fieldKey];
                                    if (!val || !val.trim()) {
                                      hasError = true;
                                    }
                                  }
                                }

                                if (hasError) {
                                  setError("Please complete all required fields. / 請完成所有必填欄位。");
                                  return;
                                }
                                setError("");
                                setStep(2);
                              }}
                              className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full hover:bg-gray-700 transition-colors cursor-pointer"
                            >
                              Next 下一步
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">核對並上傳收據 Review & Upload</h2>
                          <p className="text-sm text-gray-500 leading-relaxed mb-6">
                            核對以下資料，確認無誤後上傳付款收據。Review your details below, then upload your payment
                            receipt.
                          </p>

                          {/* Review card */}
                          <div className="rounded-2xl bg-gray-50 p-4 space-y-2 text-sm mb-5">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Ticket / 門票</span>
                              <span className="font-semibold text-gray-900">{selectedTicket?.nameZh}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Price / 價格</span>
                              <span className="font-semibold text-gray-900">{selectedTicket?.priceLabel}</span>
                            </div>
                            {appliedDiscount ? (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Discount / 折扣</span>
                                <span className="font-semibold text-green-600">
                                  -HKD {appliedDiscount.discountAmount}
                                </span>
                              </div>
                            ) : null}
                            {appliedDiscount ? (
                              <div className="flex justify-between border-t border-gray-200 pt-2">
                                <span className="text-gray-700 font-semibold">Total / 合計</span>
                                <span className="font-semibold text-gray-900">
                                  HKD {Math.max(0, (selectedTicket?.priceHkd ?? 0) - appliedDiscount.discountAmount)}
                                </span>
                              </div>
                            ) : null}
                            <div className="flex justify-between">
                              <span className="text-gray-500">Name / 姓名</span>
                              <span className="font-semibold text-gray-900">{form.fullName}</span>
                            </div>
                            {form.displayName ? (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Display / 顯示名稱</span>
                                <span className="font-semibold text-gray-900">{form.displayName}</span>
                              </div>
                            ) : null}
                            <div className="flex justify-between">
                              <span className="text-gray-500">Phone / 電話</span>
                              <span className="font-semibold text-gray-900">{form.phone}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Email / 電郵</span>
                              <span className="font-semibold text-gray-900">{form.email}</span>
                            </div>
                          </div>

                          {event.paymentMethods && event.paymentMethods.length > 0 ? (
                            <div className="mb-5">
                              <label className="block text-sm font-semibold text-gray-800 mb-3">
                                選擇付款方式 Select Payment Method *
                              </label>
                              <div className="space-y-3">
                                {event.paymentMethods.map((method) => {
                                  const isSelected = form.paymentMethod === method.name;
                                  return (
                                    <button
                                      key={method.id}
                                      type="button"
                                      onClick={() => handleChange("paymentMethod", method.name)}
                                      className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                                        isSelected
                                          ? "border-[#9B72FF] bg-[#F5F0FF]"
                                          : "border-gray-200 bg-white hover:border-gray-300"
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <span
                                          className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                                            isSelected ? "border-[#9B72FF] bg-[#9B72FF]" : "border-gray-300 bg-white"
                                          }`}
                                        />
                                        <span className="font-semibold text-gray-900">{method.name}</span>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                              {(() => {
                                const selectedMethod = event.paymentMethods?.find((m) => m.name === form.paymentMethod);
                                if (!selectedMethod || (!selectedMethod.description && !selectedMethod.photoUrl)) {
                                  return null;
                                }
                                return (
                                  <div className="mt-4 rounded-2xl bg-blue-50 border border-blue-100 p-5">
                                    <h3 className="text-sm font-bold text-gray-900 mb-2">{selectedMethod.name}</h3>
                                    {selectedMethod.description ? (
                                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                                        {selectedMethod.description}
                                      </pre>
                                    ) : null}
                                    {selectedMethod.photoUrl ? (
                                      <div className="mt-3 flex justify-center">
                                        <img
                                          src={selectedMethod.photoUrl}
                                          alt={selectedMethod.name}
                                          className="max-w-[220px] h-auto rounded-2xl border border-blue-200"
                                        />
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : null}

                          <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                              <label className="block text-sm font-semibold text-gray-800 mb-2">
                                上傳付款收據 Upload Receipt *
                              </label>
                              <label className="flex items-center justify-center border border-dashed border-gray-300 rounded-2xl px-4 py-5 text-sm text-gray-500 cursor-pointer hover:border-[#9B72FF] hover:text-gray-700 transition-colors">
                                <input type="file" accept="image/*" className="hidden" onChange={handleReceiptChange} />
                                {form.receiptFileName || "選擇檔案 Attach File / 選擇檔案"}
                              </label>
                              <p className="text-xs text-gray-400 mt-2">
                                接受圖片格式，最大 5MB / Accepted image formats only. Max 5MB.
                              </p>
                              {form.receiptDataUrl ? (
                                <div className="mt-3 rounded-2xl border border-gray-200 overflow-hidden">
                                  <img
                                    src={form.receiptDataUrl}
                                    alt="Receipt preview"
                                    className="w-full h-auto max-h-48 object-contain bg-gray-50"
                                  />
                                </div>
                              ) : null}
                            </div>

                            {error ? (
                              <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
                            ) : null}

                            <button
                              type="submit"
                              disabled={submitting}
                              className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full hover:bg-gray-700 transition-colors disabled:opacity-60 cursor-pointer"
                            >
                              {submitting ? "Submitting... / 提交中" : "提交報名 Submit Registration"}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setStep(1);
                                setError("");
                              }}
                              disabled={submitting}
                              className="w-full bg-gray-100 text-gray-700 font-semibold py-4 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              Back 返回修改
                            </button>
                          </form>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Floating register button */}
      <button
        onClick={() => {
          const el = document.getElementById("register");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className="fixed bottom-6 right-6 z-40 bg-gray-900 text-white font-semibold px-5 py-3 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-200 cursor-pointer"
      >
        Get Tickets / 購票
      </button>

      <Footer />
    </div>
  );
}
